import type { DuctedPanelSelection } from 'components/equipment-page/ducted-system-detail-panel';
import { useCallback, useMemo, useState } from 'react';
import {
  deriveDuctedSwapOptions,
  describeDuctedSwapChanges,
  DUCTED_SWAP_SLOTS,
  resolveDuctedSwap
} from 'utils/ducted-component-swap';
import {
  DuctedSlotValues,
  DuctedSwapLocks,
  DuctedSwapLockState,
  DuctedSwapOptionsBySlot,
  DuctedSwapSlot
} from 'utils/ducted-component-swap-types';
import { DEFAULT_DUCTED_SWAP_LOCKS } from 'utils/ducted-swap-locks';
import { notification } from 'utils/ui';

export type DuctedComponentSwapHandler = (
  selection: DuctedPanelSelection
) => void;

export type UseDuctedComponentSwapResult = {
  current: DuctedSlotValues;
  locks: DuctedSwapLocks;
  options: DuctedSwapOptionsBySlot;
  toggleLock: (slot: DuctedSwapSlot) => void;
  hasOtherLocks: (slot: DuctedSwapSlot) => boolean;
  swap: (slot: DuctedSwapSlot, modelKey: string) => void;
};

export const UNAVAILABLE_SWAP_MESSAGE =
  "This combination isn't available with your current filters.";

export const CHANGED_COMPONENTS_MESSAGE =
  'Some components were changed to make this system work:';

export default function useDuctedComponentSwap(
  current: DuctedSlotValues,
  candidates: DuctedPanelSelection[],
  onSwap: DuctedComponentSwapHandler | undefined,
  lockState?: DuctedSwapLockState
): UseDuctedComponentSwapResult {
  const [uncontrolledLocks, setUncontrolledLocks] = useState<DuctedSwapLocks>(
    DEFAULT_DUCTED_SWAP_LOCKS
  );
  const locks = lockState?.locks ?? uncontrolledLocks;
  const onLocksChange = lockState?.onChange ?? setUncontrolledLocks;
  const options = useMemo(
    () => deriveDuctedSwapOptions(candidates, current, locks),
    [candidates, current, locks]
  );

  const toggleLock = useCallback(
    (slot: DuctedSwapSlot) => {
      onLocksChange({ ...locks, [slot]: !locks[slot] });
    },
    [locks, onLocksChange]
  );

  const hasOtherLocks = useCallback(
    (slot: DuctedSwapSlot) =>
      DUCTED_SWAP_SLOTS.some((other) => other !== slot && locks[other]),
    [locks]
  );

  const swap = useCallback(
    (slot: DuctedSwapSlot, modelKey: string) => {
      const resolution = resolveDuctedSwap(
        candidates,
        current,
        slot,
        modelKey,
        locks
      );
      if (!resolution) {
        notification(UNAVAILABLE_SWAP_MESSAGE, 'warning');
        return;
      }
      if (resolution.changes.length > 0) {
        notification(
          `${CHANGED_COMPONENTS_MESSAGE}\n${describeDuctedSwapChanges(
            resolution.changes
          )}`,
          'warning'
        );
      }
      onSwap?.(resolution.selection);
    },
    [candidates, current, locks, onSwap]
  );

  return { current, locks, options, toggleLock, hasOtherLocks, swap };
}
